import { ApiProblem, type Api, type Schema } from '@cdn/api';
import { Pointer, Replay, type PointerMessage, type StreamEvent } from '@cdn/contracts';
import { Observable } from '@cdn/state';
import type { Handlers, Subscription } from './port';

/** `held`: it dropped while the page's session is not signed, so the session's dialog speaks and the stream does not */
export type StreamState = 'connecting' | 'live' | 'recovering' | 'held' | 'revoked' | 'closed';

/** The page's session after looking (`Session.check()` of `@beyond-js/ui`): only `signed` lets a stream speak */
export type Check = () => Promise<'signed' | 'renewing' | 'asking' | 'ended'>;

/**
 * What a consumer derives its view from: the authorized snapshot, then every later event exactly once and in
 * sequence order. A new snapshot replaces whatever was derived before it.
 */
export interface Listener {
	snapshot(snapshot: Schema.Snapshot): void;
	event(event: StreamEvent): void;
}

interface Specs {
	api: Api;
	channel: string;
	listener: Listener;
	/** Opens the transport once the stream is ready to take arrivals */
	connect: (handlers: Handlers) => Promise<Subscription>;
	/** Milliseconds a gap may stay open before a replay is requested: late arrivals often close it */
	patience?: number;
	/** Looks at the page's session when the stream drops; without it the stream always speaks for itself */
	check?: Check;
}

/**
 * One channel of beyond-cdn-events/1 on the consumer side: subscribe first, load the snapshot, then apply
 * only later events. The contracts' `Replay` deduplicates and orders; this class recovers gaps from the
 * cursor, takes a new snapshot when the cursor expired or a resync is required, and tears everything down
 * when access was revoked. Nothing is persisted in the browser: after the tab was closed, state is rebuilt
 * from the snapshot and the replay alone.
 *
 * A drop (an interrupted transport, a failed snapshot or replay) first asks the page's session (`check`): only
 * while it is `signed` is the drop an outage, which the stream says ("recovering") and retries with its bounded
 * backoff. Otherwise the session's dialog is what the person sees (D63), the stream is `held` and says nothing,
 * and `resume()` reconnects it once the session was renewed.
 */
export class Stream extends Observable {
	#api: Api;
	#listener: Listener;
	#connect: Specs['connect'];
	#patience: number;
	#check: Check | undefined;
	#replay: Replay;
	#subscription: Subscription | undefined;
	#timer: ReturnType<typeof setTimeout> | undefined;
	#busy = false;
	#interrupted = false;
	/** Consecutive failed snapshot or replay requests; each one doubles the wait before the next, up to 30 s */
	#failures = 0;

	#channel: string;
	get channel() {
		return this.#channel;
	}

	#state: StreamState = 'connecting';
	get state() {
		return this.#state;
	}

	#reason: string | undefined;
	/** Why access ended: `session`, `membership`, `guest`, `visibility`, or `denied` when the join was refused */
	get reason() {
		return this.#reason;
	}

	#problem: unknown;
	/** The last failure of a snapshot or replay request while the stream keeps trying */
	get problem() {
		return this.#problem;
	}

	get cursor() {
		return this.#replay.cursor;
	}

	#pointed: PointerMessage['reason'] | undefined;
	/** Why updates are being recovered instead of arriving live, while a pointer's gap is open */
	get pointed() {
		return this.#pointed;
	}

	constructor({ api, channel, listener, connect, patience = 400, check }: Specs) {
		super();
		this.#api = api;
		this.#channel = channel;
		this.#listener = listener;
		this.#connect = connect;
		this.#patience = patience;
		this.#check = check;
		this.#replay = new Replay({ channel });
	}

	/**
	 * Subscribes, then loads the snapshot: arrivals in between are held by the replay, so the race loses nothing.
	 * A subscription that could not be made (the grant was not answered) is tried again with the same capped
	 * backoff as a snapshot, so the stream never stays recovering with nothing left to recover it.
	 */
	async open() {
		if (this.#ended || this.#subscription) return;
		try {
			this.#subscription = await this.#connect(this.#handlers);
		} catch (problem) {
			return void this.#fail(problem, () => void this.open());
		}
		if (this.#ended) return void this.#subscription?.close();
		await this.#resync();
	}

	close() {
		if (this.#state === 'closed') return;
		this.#teardown();
		this.#state === 'revoked' || this.#move('closed');
	}

	/** The session was renewed: a stream held while it was not signed connects again and recovers what it missed */
	resume() {
		if (this.#state !== 'held') return;
		this.#failures = 0;
		if (!this.#subscription) return void this.open();
		this.#replay.cursor === undefined ? void this.#resync() : void this.#recover();
	}

	/** Ends the subscription because the service refused it */
	revoke(reason: string) {
		if (this.#ended) return;
		this.#teardown();
		this.#reason = reason;
		this.#move('revoked');
	}

	get #ended() {
		return this.#state === 'closed' || this.#state === 'revoked';
	}

	get #handlers(): Handlers {
		return {
			message: page => this.#ended || this.#receive(page),
			cursor: () => this.#replay.cursor,
			expired: () => void this.#resync(),
			revoked: () => this.revoke('denied'),
			state: state => {
				if (this.#ended) return;
				if (state === 'interrupted') {
					this.#interrupted = true;
					return void (this.#replay.cursor !== undefined && this.#falter(() => this.#interrupted, () => this.#move('recovering')));
				}
				// Notifications sent while the transport was down are lost: ask for what followed the cursor
				if (this.#interrupted && this.#replay.cursor !== undefined) void this.#recover();
				this.#interrupted = false;
			}
		};
	}

	// A pointer delivers nothing by itself: it opens a gap that `#take` closes through the replay route
	#receive(page: { events: StreamEvent[] } | PointerMessage) {
		const pointer = 'events' in page ? undefined : Pointer.read(page);
		const before = this.#replay.gap;
		const events = this.#replay.merge(page);
		if (pointer && !before && this.#replay.gap) this.#pointed = pointer.reason;
		this.#take(events);
	}

	#move(state: StreamState) {
		if (this.#state === state) return;
		this.#state = state;
		this.changed();
	}

	#teardown() {
		clearTimeout(this.#timer);
		this.#subscription?.close();
		this.#subscription = undefined;
	}

	#take(events: StreamEvent[]) {
		let resync = false;
		for (const event of events) {
			if (event.type === 'access.revoked') return this.revoke(String(event.data.reason ?? 'session'));
			resync ||= event.type === 'resync.required';
			this.#listener.event(event);
		}
		if (resync || this.#replay.stale) return void this.#resync();
		if (!this.#replay.gap) return;
		clearTimeout(this.#timer);
		this.#timer = setTimeout(() => this.#replay.gap && void this.#recover(), this.#patience);
	}

	async #resync() {
		if (this.#busy || this.#ended) return;
		this.#busy = true;
		try {
			const snapshot = await this.#api.call('realtime.snapshot', { params: { channel: this.#channel } });
			if (this.#ended) return;
			const held = this.#replay.reset(snapshot);
			this.#listener.snapshot(snapshot);
			this.#problem = undefined;
			this.#failures = 0;
			this.#move('live');
			this.#busy = false;
			this.#take(held);
		} catch (problem) {
			this.#fail(problem, () => void this.#resync());
		} finally {
			this.#busy = false;
		}
	}

	async #recover() {
		if (this.#busy || this.#ended || this.#replay.cursor === undefined) return;
		this.#busy = true;
		this.#move('recovering');
		try {
			for (let more = true; more && !this.#ended; ) {
				const page = await this.#api.call('realtime.replay', { params: { channel: this.#channel }, query: { after: this.#replay.cursor } });
				more = page.more && page.events.length > 0;
				this.#busy = false;
				this.#take(this.#replay.merge({ events: page.events as unknown as StreamEvent[] }));
				this.#busy = more;
			}
			this.#problem = undefined;
			this.#failures = 0;
			this.#pointed = undefined;
			this.#ended || (this.#move('live'), this.changed());
		} catch (problem) {
			this.#busy = false;
			if (ApiProblem.of(problem).is('CURSOR_EXPIRED')) return void this.#resync();
			this.#fail(problem, () => void this.#recover());
		} finally {
			this.#busy = false;
		}
	}

	#fail(problem: unknown, retry?: () => void) {
		if (this.#ended) return;
		if (ApiProblem.of(problem).is('ACCESS_REVOKED', 'FORBIDDEN', 'NOT_FOUND')) return this.revoke('denied');
		this.#problem = problem;
		this.#falter(
			() => this.#problem === problem,
			() => {
				this.#move('recovering');
				this.changed();
				if (!retry) return;
				clearTimeout(this.#timer);
				this.#timer = setTimeout(retry, Math.min(3000 * 2 ** this.#failures++, 30000));
			}
		);
	}

	/**
	 * A drop is said (`speak`) only while the page's session is signed; otherwise the stream is `held` until
	 * `resume()`. `still` tells whether the drop still stands once the session was looked at
	 */
	#falter(still: () => boolean, speak: () => void) {
		if (!this.#check) return speak();
		const settle = (signed: boolean) => {
			if (this.#ended || !still()) return;
			signed ? speak() : this.#move('held');
		};
		// A look that failed is no word on the session: the drop is an outage
		this.#check().then(
			state => settle(state === 'signed'),
			() => settle(true)
		);
	}
}
